import React, { useMemo, useState } from "react";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
} from "@material-ui/core";
import styled from "styled-components";
import XLSX from "xlsx-js-style";
import {
  COUNTRY_HEADERS as ASSESSMENT_COUNTRY_HEADERS,
  HEADERS as ASSESSMENT_HEADERS,
  SITE_ASSESSMENT_TYPES,
} from "../../assessments.table/table.utils";
import { INITIAL_RISK_ASSESSMENT_HEADERS } from "../../initial.risk.assessment.table/table.utils";
import {
  MAPPING_TOTAL_HEADERS,
  HEADERS as SOE_HEADERS,
} from "../../soe.table/table.utils";
import {
  COUNTRY_HEADERS as VISIT_COUNTRY_HEADERS,
  HEADERS as VISIT_HEADERS,
} from "../../visits.table/table.utils";
import { DEFAULT_DATE_CONFIG } from "../../../../../app/contexts/helpers";
import { Modal } from "../../../../../app/components";
import { useScheduleEvents } from "../../../../../app/contexts/schedule.events.context";
import PrimaryButton from "../../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../../app/components/buttons/outlined";
import { processSoeContent } from "../single.soe/excel.exports.single.soe";
import {
  mapParticipantExportContentForSheet,
  toParticipantUiText,
} from "../../../../../app/utils/participantTerminology";
import {
  styleTableContent,
  styleRiskAssessmentTableContent,
  styleVisitSamplingStrategyTableContent,
} from "./excel.exports.styles";

// Helper function to add currency to header
const addCurrencyToHeader = (header, currency) => {
  if (!currency) return header;
  return `${header} (${currency})`;
};

// Helper function to strip currency formatting from values
const stripCurrencyFromValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  // If it's a number, return as is
  if (typeof value === "number") return value;
  // If it's a string, try to extract the number
  if (typeof value === "string") {
    // Remove common currency symbols and whitespace, keep only numbers and decimal points
    const numericValue = value.replace(/[^\d.-]/g, "");
    return numericValue || "";
  }
  return value;
};

export default function ExcelExportsModal({ open, handleClose }) {
  const [isChecked, setIsChecked] = useState({});
  const [exportState, setExportState] = useState("idle");
  const {
    scheduleOfEventsVisits,
    scheduleOfEventsAssessments,
    visitsAndAssessmentsMapping,
    currentSOE,
    SOECountries,
    fetchSOECountryVisits,
    fetchSOECountryAssessments,
    totalMappings,
    initialRiskAssessments,
    initialRiskAssessmentTotal,
    visitSampleStrategy,
    variablesData,
    ePrice,
    CRA,
    IMV,
    CDA,
    CDAStudy,
    ePriceInfo,
  } = useScheduleEvents();

  // Memoized values for country and site level schedule events
  const countryLevel = useMemo(() => {
    return SOECountries.filter(
      (country) => country.siteId === 1 && country.siteGroupId !== 0,
    );
  }, [SOECountries]);

  const siteLevel = useMemo(() => {
    return SOECountries.filter(
      (country) => country.siteId !== 1 && country.siteGroupId !== 0,
    );
  }, [SOECountries]);

  // Memoized values to check if there are visits, assessments, etc.
  const hasVisits = useMemo(
    () => scheduleOfEventsVisits?.["study"]?.length > 0,
    [scheduleOfEventsVisits],
  );
  const hasAssessments = useMemo(
    () => scheduleOfEventsAssessments?.["study"]?.length > 0,
    [scheduleOfEventsAssessments],
  );

  const hasAssessmentForSOE = useMemo(
    () =>
      scheduleOfEventsAssessments?.["study"]?.filter((sa) =>
        ["Procedure", "Non Procedure"].includes(sa.assessmentType),
      ).length,
    [scheduleOfEventsAssessments],
  );

  const hasInitialRiskAssessment = useMemo(
    () => initialRiskAssessments,
    [initialRiskAssessments],
  );

  const hasVisitSamplingStrategy = useMemo(
    () => visitSampleStrategy?.length > 0,
    [visitSampleStrategy],
  );

  // Filename for the exported file based on current schedule of events
  const filename_base = useMemo(
    () =>
      `${(currentSOE.scheduleOfEventsName || "").replaceAll(
        " ",
        "_",
      )}_${new Date().toString()}_${currentSOE.studyId}`,
    [currentSOE],
  );

  const handleChange = (e, value) => {
    const { name } = e.target;
    setIsChecked((prev) => ({ ...prev, [name]: value }));
  };

  const resetModal = () => {
    setIsChecked({});
    handleClose();
  };

  const download_soe = () => {
    const soeContent = [
      ["Activities", ...SOE_HEADERS(scheduleOfEventsVisits["study"])],
    ];
    processSoeContent({
      soeContent,
      scheduleOfEventsAssessments,
      visitsAndAssessmentsMapping,
      simpleSoeReport: false,
    });

    const currency = currentSOE?.currency;
    const totals = MAPPING_TOTAL_HEADERS.map((header, idx) => {
      // Don't add currency to Unique CRFs and Non Unique CRFs rows
      const shouldAddCurrency = currency && !header.includes("CRFs") && !header.includes("Assessment Count");
      const firstColValue = shouldAddCurrency ? `${header} (${currency})` : header;
      // Strip currency from all values in the row
      const strippedValues = (totalMappings[idx] || []).map((value) => 
        stripCurrencyFromValue(value)
      );
      return [firstColValue, ...strippedValues];
    });
    return styleTableContent([...soeContent, ...totals]);
  };

  const download_country_assessments = async () => {
    const countryValues = {};
    let results = await Promise.all(
      countryLevel?.map((soeC) => {
        if (soeC.siteGroupId === 0) return null;
        if (!scheduleOfEventsAssessments[`${soeC.siteGroupId}-${soeC.siteId}`]) {
          return fetchSOECountryAssessments(
            soeC.scheduleOfEventsId,
            soeC.siteGroupId,
            soeC.siteId,
          );
        }
        return null;
      }),
    );

    results.forEach((res, idx) => {
      if (res) {
        countryValues[`${countryLevel[idx].siteGroupId}-${countryLevel[idx].siteId}`] = res;
      }
    });

    const countryAssessmentsContent = scheduleOfEventsAssessments?.["study"]?.map(
      (visit, idx) => {
        return [
          visit.assessmentType,
          visit.assessmentName,
          visit.assessmentNameMapping,
          ...countryLevel
            .filter((soc) => soc.siteGroupId !== 0)
            ?.map(
              (soc) => {
                const countryAssessment = (scheduleOfEventsAssessments?.[
                  `${soc.siteGroupId}-${soc.siteId}`
                ] || countryValues[`${soc.siteGroupId}-${soc.siteId}`])[idx];
                const assessmentCost = countryAssessment?.assessmentCost;
                // Return only the numeric value
                return stripCurrencyFromValue(assessmentCost);
              }
            ),
        ];
      },
    );

    // Update headers with currency
    const headersWithCurrency = ASSESSMENT_COUNTRY_HEADERS(countryLevel).map(
      (header, idx) => {
        // First 3 columns don't have currency
        if (idx < 3) return header;
        // For country columns, add the currency if available
        const countryIdx = idx - 3;
        if (countryLevel[countryIdx]?.currency) {
          return addCurrencyToHeader(
            header,
            countryLevel[countryIdx].currency,
          );
        }
        return header;
      },
    );

    countryAssessmentsContent.unshift(headersWithCurrency);

    return styleTableContent(countryAssessmentsContent);
  };

  const download_site_assessments = async () => {
    const siteValues = {};
    let results = await Promise.all(
      siteLevel?.map((soeC) => {
        if (soeC.siteGroupId === 0) return null;
        if (!scheduleOfEventsAssessments?.[`${soeC.siteGroupId}-${soeC.siteId}`]) {
          return fetchSOECountryAssessments(
            soeC.scheduleOfEventsId,
            soeC.siteGroupId,
            soeC.siteId,
          );
        }
        return null;
      }),
    );

    results.forEach((res, idx) => {
      if (res) {
        siteValues[`${siteLevel[idx].siteGroupId}-${siteLevel[idx].siteId}`] =
          res;
      }
    });

    const siteAssessmentsContent = scheduleOfEventsAssessments?.["study"]?.map(
      (visit, idx) => {
        return [
          visit.assessmentType,
          visit.assessmentName,
          visit.assessmentNameMapping,
          ...siteLevel
            .filter((soc) => soc.siteGroupId !== 0)
            ?.map(
              (soc) => {
                const siteAssessment = (scheduleOfEventsAssessments?.[
                  `${soc.siteGroupId}-${soc.siteId}`
                ] || siteValues[`${soc.siteGroupId}-${soc.siteId}`])[idx];
                const assessmentCost = siteAssessment?.assessmentCost;
                // Return only the numeric value
                return stripCurrencyFromValue(assessmentCost);
              }
            ),
        ];
      },
    );

    // Update headers with currency
    const headersWithCurrency = ASSESSMENT_COUNTRY_HEADERS(siteLevel).map(
      (header, idx) => {
        // First 3 columns don't have currency
        if (idx < 3) return header;
        // For site columns, add the currency if available
        const siteIdx = idx - 3;
        if (siteLevel[siteIdx]?.currency) {
          return addCurrencyToHeader(header, siteLevel[siteIdx].currency);
        }
        return header;
      },
    );

    siteAssessmentsContent.unshift(headersWithCurrency);
    return styleTableContent(siteAssessmentsContent);
  };

  const download_assessments = () => {
    const assessmentContent = scheduleOfEventsAssessments?.["study"]?.map(
      (item) => {
        const isSiteCostType = SITE_ASSESSMENT_TYPES.includes(
          item.assessmentType,
        );
        return [
          item.assessmentType,
          item.assessmentName,
          item.assessmentNameMapping,
          isSiteCostType ? "-" : item.overhead ? "X" : "",
          item.commentsAssessment || "",
          item.commentsCost || "",
          item.commentsClinicalStrategy || "",
          item.commentsDataManagement || "",
        ];
      },
    );

    const headersWithCurrency = Object.values(ASSESSMENT_HEADERS);
    assessmentContent.unshift(headersWithCurrency);
    return styleTableContent(assessmentContent);
  };

  const download_country_visits = async () => {
    const countryValues = {};
    let results = await Promise.all(
      countryLevel?.map((soeC) => {
        if (soeC.siteGroupId === 0) return null;
        if (!scheduleOfEventsVisits?.[`${soeC.siteGroupId}-${soeC.siteId}`]) {
          return fetchSOECountryVisits(
            soeC.scheduleOfEventsId,
            soeC.siteGroupId,
            soeC.siteId,
          );
        }
        return null;
      }),
    );
    results.forEach((res, idx) => {
      if (res) {
        countryValues[
          `${countryLevel[idx].siteGroupId}-${countryLevel[idx].siteId}`
        ] = res;
      }
    });

    const countryVisitsContent = scheduleOfEventsVisits?.["study"]?.map(
      (visit, idx) => {
        return [
          visit.visitCategory,
          visit.visitName,
          visit.visitStudyDay,
          visit.visitVarianceType,
          visit.visitVariance,
          ...countryLevel
            .filter((soc) => soc.siteGroupId !== 0)
            ?.map((soc) => {
              const visitData = (scheduleOfEventsVisits?.[
                `${soc.siteGroupId}-${soc.siteId}`
              ] || countryValues[`${soc.siteGroupId}-${soc.siteId}`])[idx];
              const visitCostTotal = visitData?.visitCostTotal ?? "";
              // Return only the numeric value
              return stripCurrencyFromValue(visitCostTotal);
            }),
        ];
      },
    );

    // Update headers with currency
    const headersWithCurrency = VISIT_COUNTRY_HEADERS(
      currentSOE,
      countryLevel,
    ).map((header, idx) => {
      // First 5 columns don't have currency
      if (idx < 5) return header;
      // For country columns, add the currency if available
      const countryIdx = idx - 5;
      if (countryLevel[countryIdx]?.currency) {
        return addCurrencyToHeader(
          header,
          countryLevel[countryIdx].currency,
        );
      }
      return header;
    });

    countryVisitsContent.unshift(headersWithCurrency);
    return styleTableContent(countryVisitsContent);
  };

  const download_site_visits = async () => {
    const countryValues = {};
    let results = await Promise.all(
      siteLevel?.map((soeC) => {
        if (soeC.siteGroupId === 0) return null;
        if (!scheduleOfEventsVisits?.[`${soeC.siteGroupId}-${soeC.siteId}`]) {
          return fetchSOECountryVisits(
            soeC.scheduleOfEventsId,
            soeC.siteGroupId,
            soeC.siteId,
          );
        }
        return null;
      }),
    );

    results.forEach((res, idx) => {
      if (res) {
        countryValues[
          `${siteLevel[idx].siteGroupId}-${siteLevel[idx].siteId}`
        ] = res;
      }
    });

    const siteVisitsContent = scheduleOfEventsVisits?.["study"]?.map(
      (visit, idx) => {
        return [
          visit.visitCategory,
          visit.visitName,
          visit.visitStudyDay,
          visit.visitVarianceType,
          visit.visitVariance,
          ...siteLevel
            .filter((soc) => soc.siteGroupId !== 0)
            ?.map((soc) => {
              const siteVisit = (scheduleOfEventsVisits?.[
                `${soc.siteGroupId}-${soc.siteId}`
              ] || countryValues[`${soc.siteGroupId}-${soc.siteId}`])[idx];
              const visitCostTotal = siteVisit?.visitCostTotal;
              // Return only the numeric value
              return stripCurrencyFromValue(visitCostTotal);
            }),
        ];
      },
    );

    // Update headers with currency
    const headersWithCurrency = VISIT_COUNTRY_HEADERS(
      currentSOE,
      siteLevel,
    ).map((header, idx) => {
      // First 5 columns don't have currency
      if (idx < 5) return header;
      // For site columns, add the currency if available
      const siteIdx = idx - 5;
      if (siteLevel[siteIdx]?.currency) {
        return addCurrencyToHeader(header, siteLevel[siteIdx].currency);
      }
      return header;
    });

    siteVisitsContent.unshift(headersWithCurrency);
    return styleTableContent(siteVisitsContent);
  };

  const download_visits = () => {
    const visitsContent = scheduleOfEventsVisits?.["study"]?.map((item) => {
      return [
        item.visitCategory || "",
        item.visitName || "",
        item.visitStudyDay || "",
        item.visitVarianceType || "",
        item.visitVariance || "",
        item.miEstSDRSDV || "",
        item.miSampleVisit ? "X" : "",
        item.miSuggestedOnSiteSDRSDV || "",
        item.miSuggestedCDAReview || "",
        item.dmUniqueCRFs || "",
        item.dmNonUniqueCRFs || "",
        item.commentsVisit || "",
        item.commentsCost || "",
        item.commentsClinicalStrategy || "",
        item.commentsDataManagement || "",
      ];
    });

    const headersWithCurrency = Object.values(VISIT_HEADERS(currentSOE));
    visitsContent.unshift(headersWithCurrency);
    return styleTableContent(visitsContent);
  };

  const download_risk_assessment = () => {
    const riskAssessmentContent = initialRiskAssessments?.map((item) => {
      return [
        item.riskAssessmentCategory || "",
        item.riskAssessmentStudySpecifics || "",
        item.riskAssessmentRiskScore || "",
        item.riskAssessmentLow || "",
        item.riskAssessmentModerate || "",
        item.riskAssessmentHigh || "",
      ];
    });
    riskAssessmentContent.unshift([
      ...Object.values(INITIAL_RISK_ASSESSMENT_HEADERS),
    ]);
    riskAssessmentContent.push([
      "",
      initialRiskAssessmentTotal.label,
      initialRiskAssessmentTotal.total,
      "",
      "",
      "",
    ]);
    return styleRiskAssessmentTableContent(riskAssessmentContent);
  };

  const download_visit_sampling_strategy = () => {
    const visitSamplingStrategyContent = [];

    // header placeholders
    visitSamplingStrategyContent.push([null, null, null, null, null, null]);
    variablesData.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        "",
        "",
        "",
        item.notes,
      ]);
    });

    visitSamplingStrategyContent.push(["", "", "", "", "", ""]);
    visitSamplingStrategyContent.push([null, null, null, null, null, null]);
    ePrice.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        "",
        "",
        "",
        item.notes,
      ]);
    });

    visitSamplingStrategyContent.push(["", "", "", "", "", ""]);
    visitSamplingStrategyContent.push([
      "",
      "100% (traditional)",
      "Visit Sampling Modeling",
      "Difference from Traditional",
      "",
      "Notes",
    ]);
    CRA.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        item.cellValue2,
        item.cellValue3,
        "",
        item.notes,
      ]);
    });

    visitSamplingStrategyContent.push(["", "", "", "", "", ""]);
    visitSamplingStrategyContent.push([null, null, null, null, null, null]);
    IMV.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        item.cellValue2,
        item.cellValue3,
        "",
        item.notes,
      ]);
    });
    visitSamplingStrategyContent.push(["", "", "", "", "", ""]);
    visitSamplingStrategyContent.push(["", "Number", "Hours", "", "", "Notes"]);
    CDA.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        item.cellValue2,
        item.cellValue3,
        "",
        item.notes,
      ]);
    });
    visitSamplingStrategyContent.push(["", "", "", "", "", ""]);
    visitSamplingStrategyContent.push(["", "Number", "Hours", "", "", "Notes"]);
    CDAStudy.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        item.cellValue2,
        item.cellValue3,
        "",
        item.notes,
      ]);
    });
    visitSamplingStrategyContent.push(["", "", "", "", "", ""]);
    visitSamplingStrategyContent.push([
      "",
      "Visit Sampling SDR/SDV Time (reduced SDR/SDV)",
      "100% SDR/SDV",
      "Data Review (CDA): KRI Review and Study Level Reporting Only",
      "SDR/SDV % Participant Visits",
      "Notes",
    ]);
    ePriceInfo.forEach((item) => {
      visitSamplingStrategyContent.push([
        toParticipantUiText(item.rowLabel),
        item.cellValue1,
        item.cellValue2,
        item.cellValue3,
        item.cellValue4,
        item.notes,
      ]);
    });

    return styleVisitSamplingStrategyTableContent(visitSamplingStrategyContent);
  };

  const exportAsCSV = async () => {
    setExportState("exporting");
    try {
      const wb = XLSX.utils.book_new();

      for (const [exportType, isSelected] of Object.entries(isChecked)) {
        if (!isSelected) continue;
        
        let content, sheet, sheetName, sheetCols, dateConfig = {};
        
        switch (exportType) {
          case 'visits':
            content = download_visits();
            dateConfig = DEFAULT_DATE_CONFIG;
            sheetName = "Visits Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index === 0 ? { width: 15 } : { width: 20 }
              );
            }
            break;

          case 'countryVisits':
            content = await download_country_visits();
            sheetName = "Country Visits Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index < 5 ? { width: 15 } : { width: 50 }
              );
            }
            break;

          case 'siteVisits':
            content = await download_site_visits();
            sheetName = "Site Visits Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index < 5 ? { width: 15 } : { width: 50 }
              );
            }
            break;

          case 'assessments':
            content = download_assessments();
            sheetName = "Assessments Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index === 0 ? { width: 15 } : { width: 20 }
              );
            }
            break;

          case 'countryAssessments':
            content = await download_country_assessments();
            sheetName = "Country Assessments Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index < 3 ? { width: 15 } : { width: 50 }
              );
            }
            break;

          case 'siteAssessments':
            content = await download_site_assessments();
            sheetName = "Site Assessments Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index < 3 ? { width: 15 } : { width: 50 }
              );
            }
            break;

          case 'soe':
            content = download_soe();
            sheetName = "SOE Table";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                index === 0 ? { width: 40 } : { width: 20 }
              );
            }
            break;

          case 'initialRiskAssessment':
            content = download_risk_assessment();
            dateConfig = DEFAULT_DATE_CONFIG;
            sheetName = "Initial Risk Assessment";
            if (content.length > 0) {
              sheetCols = content[0].map((_, index) =>
                [0, 1, 2].includes(index) ? { width: 15 } : { width: 40 }
              );
            }
            break;

          case 'visitSamplingStrategy':
            content = download_visit_sampling_strategy();
            sheetName = "Visit Sampling Strategy";
            if (content.length > 0) {
              sheetCols = [1, 2, 3, 4, 5, 6].map((idx) =>
                idx === 1 ? { width: 60 } : idx === 6 ? { width: 80 } : { width: 20 }
              );
            }
            break;

          default:
            continue;
        }

        sheet = XLSX.utils.aoa_to_sheet(
          mapParticipantExportContentForSheet(content, exportType),
          dateConfig,
        );
        if (sheetCols) {
          sheet["!cols"] = sheetCols;
        }
        XLSX.utils.book_append_sheet(wb, sheet, sheetName);
      }

      XLSX.writeFile(wb, `${filename_base}.xlsx`, XLSX.cellDates);

      setIsChecked({});
      setExportState("exported");
      handleClose();
    } catch (err) {
      console.log(err);
      setExportState("idle");
    }
  };

  const validExportValues = () => {
    const values = Object.values(isChecked);
    if (values.length === 0) return false;
    if (values.find((v) => v === true)) return true;
    return false;
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (exportState !== "exporting") resetModal();
      }}
      aria-labelledby="Export Tables"
      aria-describedby="Export Tables"
      title="Export Tables"
      sx={{ minWidth: "30rem", maxWidth: "100%", width: "70rem" }}
    >
      <FormGroup>
        <Container>
          <SubContainer>
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasVisits}
                  checked={!!isChecked.visits}
                  onChange={handleChange}
                  name="visits"
                />
              }
              label={`Visits Table`}
            />
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasAssessments}
                  checked={!!isChecked.assessments}
                  onChange={handleChange}
                  name="assessments"
                />
              }
              label={`Assessments Table`}
            />
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasAssessmentForSOE || !hasVisits}
                  checked={!!isChecked.soe}
                  onChange={handleChange}
                  name="soe"
                />
              }
              label={`Export SOE`}
            />
          </SubContainer>
          <SubContainer>
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasVisits}
                  checked={!!isChecked.countryVisits}
                  onChange={handleChange}
                  name="countryVisits"
                />
              }
              label={`Country Visits Table`}
            />
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasAssessments}
                  checked={!!isChecked.countryAssessments}
                  onChange={handleChange}
                  name="countryAssessments"
                />
              }
              label={`Country Assessments Table`}
            />
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasInitialRiskAssessment}
                  checked={!!isChecked.initialRiskAssessment}
                  onChange={handleChange}
                  name="initialRiskAssessment"
                />
              }
              label={`Initial Risk Assessment`}
            />
          </SubContainer>
          <SubContainer>
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasVisits}
                  checked={!!isChecked.siteVisits}
                  onChange={handleChange}
                  name="siteVisits"
                />
              }
              label={`Site Visits Table`}
            />
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasAssessments}
                  checked={!!isChecked.siteAssessments}
                  onChange={handleChange}
                  name="siteAssessments"
                />
              }
              label={`Site Assessments Table`}
            />
            <FormControlLabel
              style={{ width: "30%" }}
              control={
                <Checkbox
                  disabled={!hasVisitSamplingStrategy}
                  checked={!!isChecked.visitSamplingStrategy}
                  onChange={handleChange}
                  name="visitSamplingStrategy"
                />
              }
              label={`Visit Sampling Strategy`}
            />
          </SubContainer>
        </Container>
        <ButtonContainer>
          <OutlinedButton onClick={resetModal}>Cancel</OutlinedButton>
          <PrimaryButton
            disabled={!validExportValues() || exportState === "exporting"}
            onClick={exportAsCSV}
          >
            {exportState === "exporting" ? (
              <CircularProgress
                style={{ color: "var(--White)" }}
                size={"1em"}
              />
            ) : (
              "Export"
            )}
          </PrimaryButton>
        </ButtonContainer>
      </FormGroup>
    </Modal>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const SubContainer = styled.div`
  display: flex;
  gap: 1em;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1em;
`;